import React from 'react';
import { useNavigate } from 'react-router-dom';
import deleteIcon from '../../../shared/assets/svg/delete.svg';
import { Building2, MapPin, ShieldCheck } from 'lucide-react';
import { navigateWithReturn } from '../../../shared/navigation';

const CompanyCard = ({ data, onRequestDelete, isDeleteSubmitting = false }) => {

  const navigate = useNavigate();

  const handleViewCompany = () => {
    if (data?.id) {
      navigateWithReturn(navigate, `/builder/management/company/${data.id}`, {
        returnTo: '/builder/management/company'
      });
    }
  };

  const handleViewProjects = () => {
    if (data?.id) {
      navigateWithReturn(navigate, `/builder/management/company/${data.id}/projects`, {
        returnTo: '/builder/management/company'
      });
    }
  };

  const logoUrl = data?.company_logo?.[0]?.url || null;
  const companyInitials = (data?.company_name || 'CO')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
  const initialColorThemes = [
    'bg-[#E7F2EF] border-[#BFE6DE] text-[#2A565E]',
    'bg-[#EEF2FF] border-[#D6DBFF] text-[#4F46E5]',
    'bg-[#FFF4E8] border-[#FBD7B2] text-[#B45309]',
    'bg-[#F3E8FF] border-[#E2CCFF] text-[#7E22CE]',
    'bg-[#EAF6FF] border-[#C5E4FA] text-[#0369A1]'
  ];
  const companyNameSeed = (data?.company_name || 'CO').split('').reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  const initialsTheme = initialColorThemes[companyNameSeed % initialColorThemes.length];
  const location = [data?.city, data?.state].filter(Boolean).join(', ') || data?.address || 'Location not added';
  const hasGst = Boolean(data?.gst);
  const gstLabel = hasGst ? `GST: ${data.gst}` : 'GST: Not added';
  const maskedPan = data?.pan ? `PAN: ${data.pan}` : 'PAN: Not added';

  return (
    <div className="group bg-white w-full min-h-[220px] rounded-2xl border border-[#E5EAF1] p-4 flex flex-col gap-2.5 relative shadow-sm transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#D8DEE6] motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:hover:shadow-sm">
      <button
        type="button"
        className="bg-[#F2F5F8] w-[fit-content] p-1 rounded-full cursor-pointer absolute top-3 right-3 z-10"
        onClick={(e) => {
          e.stopPropagation();
          onRequestDelete?.(data?.id);
        }}
        disabled={!data?.id || isDeleteSubmitting}>
        <img src={deleteIcon} alt="" />
      </button>

      <button
        type="button"
        aria-label="View company"
        title="View company"
        disabled={!data?.id}
        onClick={(e) => {
          e.stopPropagation();
          handleViewCompany();
        }}
        className="absolute top-3 right-12 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-[#E5EAF1] bg-white text-[#4D6C72] shadow-sm transition-colors hover:bg-[#F7FAFB] disabled:cursor-not-allowed disabled:opacity-50">
        <Building2 size={18} strokeWidth={2} />
      </button>

      <div
        role="button"
        tabIndex={data?.id ? 0 : -1}
        aria-label="View projects"
        aria-disabled={!data?.id}
        onClick={() => {
          if (data?.id) handleViewProjects();
        }}
        onKeyDown={(e) => {
          if (!data?.id) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleViewProjects();
          }
        }}
        className={`flex flex-col gap-2.5 rounded-xl outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-[#4D6C72] transition-colors duration-300 ${
          data?.id ? 'cursor-pointer group-hover:bg-[#FAFBFC]/80' : 'cursor-not-allowed opacity-60'
        }`}>
      <div className="flex items-start gap-3 pr-20">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt="Company logo"
            className="h-14 w-14 rounded-xl object-contain p-1 bg-[#F2F5F8] border border-[#E5EAF1]"
          />
        ) : (
          <div
            className={`h-14 w-14 rounded-xl border flex items-center justify-center text-2xl font-semibold ${initialsTheme}`}>
            {companyInitials || 'CO'}
          </div>
        )}
        <div className="min-w-0">
          <p
            className="text-[20px] leading-tight font-semibold text-[#111827] truncate"
            title={data?.company_name || 'Company Name Not Available'}>
            {data?.company_name || 'Company Name Not Available'}
          </p>
          <p className="text-sm text-[#6B7280] mt-1 truncate">
            {data?.company_type || 'Company Type Not Specified'}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 text-base text-[#4B5563]">
        <MapPin size={16} className="text-[#64748B] shrink-0" />
        <span className="truncate">{location}</span>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[13px] font-semibold ${
            hasGst ? 'bg-[#EAF8F2] text-[#0F766E]' : 'bg-[#F3F4F6] text-[#6B7280]'
          }`}>
          <ShieldCheck size={12} />
          {gstLabel}
        </span>
        <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[13px] font-semibold bg-[#EEF2FF] text-[#4F46E5]">
          {maskedPan}
        </span>
      </div>
      </div>
    </div>
  );
};

export default CompanyCard;
